import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me, Permission } from '@itvisits/shared';
import { api, ApiError } from '../api/client';

export type SessionStatus =
  'loading' | 'anonymous' | 'not-provisioned' | 'no-permissions' | 'authenticated';

export interface Session {
  status: SessionStatus;
  me: Me | null;
  can: (p: Permission) => boolean;
  /** Error message from a failed login attempt (e.g. NOT_PROVISIONED text). */
  loginError: string | null;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export const ME_QUERY_KEY = ['me'] as const;

export function deriveStatus(
  me: Me | null | undefined,
  error: unknown,
  isPending: boolean,
  loginError: { code: string } | null,
): SessionStatus {
  if (loginError?.code === 'NOT_PROVISIONED') return 'not-provisioned';
  if (isPending) return 'loading';
  if (error instanceof ApiError && error.status === 401) return 'anonymous';
  if (!me) return 'anonymous';
  return me.permissions.length === 0 ? 'no-permissions' : 'authenticated';
}

export function SessionProvider(props: {
  children: ReactNode;
  loginError?: { code: string; message: string } | null;
}): React.JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api.getMe(),
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    staleTime: 60_000,
  });
  const loginError = props.loginError ?? null;
  const value = useMemo<Session>(() => {
    const me = query.data ?? null;
    return {
      status: deriveStatus(me, query.error, query.isPending, loginError),
      me,
      can: (p) => me?.permissions.includes(p) ?? false,
      loginError: loginError?.message ?? null,
      refresh: async () => {
        await qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      },
    };
  }, [query.data, query.error, query.isPending, loginError, qc]);
  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession must be used inside SessionProvider');
  return s;
}

/** Test seam: provide a fixed session without React Query. */
export function StaticSession(props: { value: Session; children: ReactNode }): React.JSX.Element {
  return <SessionContext.Provider value={props.value}>{props.children}</SessionContext.Provider>;
}
